import React, { useState } from "react";
import { Lock, Eye, Loader2, StickyNote } from "lucide-react";

// Type pill — reserved-colour chip that names the conversation type. Uppercase, mono, bordered so
// it survives greyscale/colour-blindness (never colour alone).
export function ConvTypePill({ theme, label, testid }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center border font-mono uppercase tracking-widest text-[9px] px-2 py-1 whitespace-nowrap leading-none shrink-0"
      style={{ background: theme.pill.bg, borderColor: theme.pill.border, color: theme.pill.text }}
    >
      {label}
    </span>
  );
}

// Full themed header bar (solid type colour) + optional subheader line. `actions` renders on the
// right (assign/resolve/etc). Header has NO bottom border for external skins (per spec).
export function ConversationHeader({ theme, labels, title, subtitle, actions, testid }) {
  return (
    <div data-testid={testid}>
      <div className="px-4 py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3" style={{ background: theme.headerBg }}>
        <div className="min-w-0 flex-1 flex items-center gap-2.5">
          <ConvTypePill theme={theme} label={labels.pill} testid="conv-type-pill" />
          <div className="min-w-0 flex-1">
            <h3 className="font-heading uppercase tracking-tight leading-tight line-clamp-2 break-words" style={{ color: theme.headerText }}>{title}</h3>
            {subtitle && <p className="font-mono text-[10px] truncate" style={{ color: theme.headerText, opacity: 0.75 }}>{subtitle}</p>}
          </div>
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
      {labels.sub && (
        <div
          data-testid="conv-subheader"
          className="px-4 py-1.5 font-mono text-[10px] uppercase tracking-wide"
          style={{ background: theme.subheaderBg, color: theme.subheaderText, borderBottom: `1px solid ${theme.subheaderBorder}` }}
        >
          {labels.sub}
        </div>
      )}
    </div>
  );
}

// EXTERNAL windows state PLAINLY, directly above the composer, that the other side sees what you
// write. This is the mistake worth engineering against (an internal remark reaching a customer).
export function SharesNotice({ theme, recipient }) {
  return (
    <div
      data-testid="conv-shares-notice"
      className="px-4 py-1.5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-wide"
      style={{ background: theme.subheaderBg, color: theme.subheaderText, borderTop: `1px solid ${theme.subheaderBorder}` }}
    >
      <Eye className="w-3.5 h-3.5 shrink-0" />
      <span>{recipient} sees everything you send here.</span>
    </div>
  );
}

// INTERNAL-NOTES RAIL — a SEPARATE composer with its own storage, olive/internal treatment, that
// NEVER sends to the customer. Two composers, never one: an internal note cannot reach a member by
// a misclick. Staff-surfaces only.
export function InternalNotesRail({ notes = [], onAdd, fmtTs }) {
  const I = CONV_INTERNAL_THEME;
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { await onAdd(text.trim()); setText(""); } finally { setBusy(false); }
  };
  return (
    <div data-testid="internal-notes-rail" style={{ background: I.bg, borderTop: `2px solid ${I.accent}` }}>
      <div className="px-4 py-1.5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest" style={{ color: I.accentText }}>
        <Lock className="w-3.5 h-3.5" /> Internal · staff only · never sent
      </div>
      {notes.length > 0 && (
        <div className="px-4 pb-2 space-y-1.5 max-h-32 overflow-y-auto" data-testid="internal-notes-list">
          {notes.map((n) => (
            <div key={n.note_id} className="text-xs font-body" style={{ color: I.body }} data-testid={`internal-note-${n.note_id}`}>
              <span className="font-mono text-[10px]" style={{ color: I.author }}>@{n.author_username}</span>
              <span className="font-mono text-[9px] ml-1.5" style={{ color: I.meta }}>{fmtTs ? fmtTs(n.created_at) : ""}</span>
              <div className="whitespace-pre-wrap break-words mt-0.5">{n.body}</div>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={submit} className="px-3 pb-3 flex gap-2" data-testid="internal-note-composer">
        <input
          value={text} onChange={(e) => setText(e.target.value)}
          data-testid="internal-note-input"
          placeholder="Add an internal note (not sent to anyone)…"
          className="flex-1 outline-none px-3 py-2 text-sm transition-colors"
          style={{ background: I.inputBg, border: `1px solid ${I.inputBorder}`, color: I.body }}
        />
        <button
          type="submit" disabled={busy || !text.trim()} data-testid="internal-note-add-btn"
          className="font-mono uppercase text-[11px] tracking-wider px-3 py-2 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
          style={{ background: I.accent, border: `1px solid ${I.accentBorder}`, color: I.accentText }}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <StickyNote className="w-3.5 h-3.5" />} Add note
        </button>
      </form>
    </div>
  );
}

const CONV_INTERNAL_THEME = {
  bg: "#141619", accent: "#57682A", accentBorder: "#657A32", accentText: "#EDF2DE",
  body: "#D6D8DC", author: "#AEC07B", meta: "#76797D",
  inputBg: "#0F1113", inputBorder: "#2A2E24",
};
